import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api, { getUserAvatarUrl } from '../utils/api';
import { prepareFileForUpload } from '../utils/imageUploadCompress';
import { isCrmRole } from '../utils/crmPermissions';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Camera, CheckCircle2, Mail, Phone, Settings, XCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import VerifyMobileDialog from './profile/VerifyMobileDialog';
import { formatDateTime } from '../utils/appDateTime';

function formatVerifiedAt(value) {
  if (!value) return null;
  const formatted = formatDateTime(value);
  return formatted === '—' ? value : formatted;
}

export default function Profile() {
  const { user, canOpenSettings, patchUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const { toast } = useToast();
  const photoInputRef = useRef(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyMode, setVerifyMode] = useState('verify');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoCacheKey, setPhotoCacheKey] = useState('');

  if (!user) {
    navigate('/login');
    return null;
  }

  const canUploadPhoto = isCrmRole(user.role_slug);
  const userAvatarUrl = getUserAvatarUrl(user.avatar, photoCacheKey);
  const initials = [user.first_name?.[0], user.last_name?.[0]].filter(Boolean).join('').toUpperCase() || 'U';
  const isPhoneVerified = !!user.phone_verified || !!user.phone_verified_at;

  const applyTheme = async (next) => {
    try {
      await setTheme(next);
      toast({ title: 'Saved', description: `Theme set to ${next}` });
    } catch {
      toast({ title: 'Error', description: 'Could not save theme', variant: 'destructive' });
    }
  };

  const handlePhotoClick = () => {
    if (!canUploadPhoto || uploadingPhoto) return;
    photoInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please choose an image (JPG, PNG, GIF, WebP).', variant: 'destructive' });
      e.target.value = '';
      return;
    }
    setUploadingPhoto(true);
    try {
      const prepared = await prepareFileForUpload(file);
      const formData = new FormData();
      formData.append('photo', prepared);
      const res = await api.post('/auth/me/photo', formData);
      const newAvatar = res.data?.data?.avatar;
      if (newAvatar) {
        setPhotoCacheKey(String(Date.now()));
        patchUser({ avatar: newAvatar });
        toast({ title: 'Photo updated', description: 'Profile photo has been saved.' });
      }
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err.response?.data?.message || 'Could not upload photo.',
        variant: 'destructive',
      });
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    setSavingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast({ title: 'Password updated', description: 'Use your new password next time you log in.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Could not change password',
        variant: 'destructive',
      });
    } finally {
      setSavingPassword(false);
    }
  };

  const openVerifyDialog = (mode) => {
    setVerifyMode(mode);
    setVerifyDialogOpen(true);
  };

  const handleVerified = (payload, options = {}) => {
    const userPayload = payload?.id ? payload : {
      phone: payload.phone,
      phone_verified: payload.phone_verified ?? !options.pending,
      phone_verified_at: payload.phone_verified_at ?? (options.pending ? null : new Date().toISOString()),
    };
    patchUser(userPayload);
    if (!options.pending) {
      toast({
        title: 'Mobile verified',
        description: 'Your mobile number has been verified successfully.',
      });
    }
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-foreground">My Profile</h1>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            {canUploadPhoto ? (
              <>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={handlePhotoChange}
                />
                <button
                  type="button"
                  onClick={handlePhotoClick}
                  disabled={uploadingPhoto}
                  className="relative h-20 w-20 shrink-0 rounded-full border-2 border-border overflow-hidden focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background hover:opacity-90 transition-opacity disabled:opacity-50"
                  aria-label="Upload profile photo"
                >
                  <Avatar className="h-full w-full">
                    {userAvatarUrl ? (
                      <AvatarImage src={userAvatarUrl} alt={user.first_name} className="object-cover" />
                    ) : null}
                    <AvatarFallback className="bg-muted text-foreground text-2xl">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity rounded-full">
                    <Camera className="w-6 h-6 text-white" />
                  </span>
                  {uploadingPhoto && (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full">
                      <span className="animate-spin rounded-full h-7 w-7 border-2 border-white border-t-transparent" />
                    </span>
                  )}
                </button>
              </>
            ) : (
              <Avatar className="h-20 w-20 border-2 border-border">
                {userAvatarUrl ? (
                  <AvatarImage src={userAvatarUrl} alt={user.first_name} />
                ) : null}
                <AvatarFallback className="bg-muted text-foreground text-2xl">
                  {initials}
                </AvatarFallback>
              </Avatar>
            )}
            <div>
              <CardTitle className="text-xl text-foreground">
                {user.first_name} {user.last_name}
              </CardTitle>
              <p className="text-muted-foreground">{user.role}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 text-foreground">
            <Mail className="h-5 w-5 text-muted-foreground" />
            <span>{user.email || '—'}</span>
          </div>
          <div className="rounded-lg border border-border p-4 space-y-3">
            <Label className="text-foreground">Appearance</Label>
            <p className="text-sm text-muted-foreground">Same as navbar toggle; saved to your account.</p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={theme === 'light' ? 'default' : 'outline'}
                size="sm"
                onClick={() => applyTheme('light')}
              >
                Light
              </Button>
              <Button
                type="button"
                variant={theme === 'dark' ? 'default' : 'outline'}
                size="sm"
                onClick={() => applyTheme('dark')}
              >
                Dark
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Mobile Number Verification</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3">
            <Phone className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
            <div className="space-y-2 flex-1">
              <div>
                <p className="text-sm text-muted-foreground">Mobile number</p>
                <p className="text-foreground font-medium">{user.phone || 'Not set'}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm text-muted-foreground">Verification status</p>
                {isPhoneVerified ? (
                  <Badge variant="default" className="gap-1 bg-emerald-600 hover:bg-emerald-600">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Verified
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="gap-1 text-destructive border-destructive/30">
                    <XCircle className="h-3.5 w-3.5" />
                    Not Verified
                  </Badge>
                )}
              </div>
              {isPhoneVerified && user.phone_verified_at && (
                <p className="text-sm text-muted-foreground">
                  Verified on {formatVerifiedAt(user.phone_verified_at)}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {(!user.phone || !isPhoneVerified) && (
              <Button type="button" onClick={() => openVerifyDialog('verify')}>
                Verify Mobile Number
              </Button>
            )}
            {user.phone && (
              <Button type="button" variant="outline" onClick={() => openVerifyDialog('change')}>
                Change Mobile Number
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Change password</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={changePassword} className="space-y-3">
            <div>
              <Label>Current password</Label>
              <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required autoComplete="current-password" />
            </div>
            <div>
              <Label>New password</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} autoComplete="new-password" />
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} autoComplete="new-password" />
            </div>
            <Button type="submit" disabled={savingPassword}>
              {savingPassword ? 'Saving…' : 'Update password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {canOpenSettings && canOpenSettings() && (
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => navigate('/settings')}
            variant="outline"
            className="border-border text-foreground hover:bg-accent"
          >
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>
      )}

      <VerifyMobileDialog
        open={verifyDialogOpen}
        onOpenChange={setVerifyDialogOpen}
        initialPhone={verifyMode === 'change' ? '' : (user.phone || '')}
        mode={verifyMode}
        onVerified={handleVerified}
      />
    </div>
  );
}
